import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountManagementService } from '../shared/services/account-management.service';

import {
  VueAuthenticationService,
  VueFormError,
  VueSecurityQuestionAnswer,
  VueSecurityQuestionFormOutput,
  VueSecurityQuestionPromptGroup,
  VueServerMessage,
  VueServerMessageType,
  VueTranslationService
} from '@vue/vueframework';
import { PasswordResetDetails } from '../shared/types/password-reset-details';
import { AppDefaultsService } from '../app-defaults.service';

/**
 * This component is for a user to reset
 * their password
 *
 * It is normally placed on a route
 * path: 'resetPassword'
 *
 * and it made to communicate with the login route
 * path: 'authenticate
 *
 */
@Component({
  selector: 'forgot-password',
  styleUrls: [ './forgot-password.component.scss' ],
  templateUrl: './forgot-password.component.html',
  standalone: false
})
export class ForgotPasswordComponent {
  /**
     * NgForm object holding the validation form details
     */
  @ViewChild('initiatePasswordResetForm') public initiatePasswordResetForm: NgForm;
  public formError = new VueFormError();

  /**
     * list of questions the user needs to answer
     */
  public questionPromptGroups: VueSecurityQuestionPromptGroup[];

  /**
     * This holds the answers to the security questions provided
     * by the user
     */
  public securityQuestionAnswers: VueSecurityQuestionAnswer[];

  /**
     * This is a two step processes. Verify user
     * then answer security questions. Start index 0 of [0,1]
     */
  public stepIndex = 0;

  /**
   * Page level messages displayed to the user
   */
  public messages: VueServerMessage[] = [];

  /**
   * Site ID given by the user
   */
  public inputSiteID: number;

  /**
   * Username given by the user
   */
  public inputUserName: string;

  /**
     * UserId fetched after userverification
     */
  public appUserID: number;

  /**
     * UserEmai fetched after userverification
     */
  public userEmail: string;

  public appDefaultsService: AppDefaultsService;

  /**
     * specify if default isEmailSelection  is selected to reset password
     */

  public isEmailSelection = 'true';

  public constructor(
    private router: Router,
    defaultsService: AppDefaultsService,
    private authenticationService: VueAuthenticationService,
    private translationService: VueTranslationService,
    private accountManagementService: AccountManagementService,

  ) {
    this.appDefaultsService = defaultsService;
  }

  /**
   * Navigates back to the Login Page
   */
  public cancel(): void {
    this.goToLogin();
  }

  /**
   * Navigates back to the Login Page
   * @param showSuccessMessage flag for whether success message should be shown in login page
   */
  public goToLogin(showSuccessMessage = false): void {
    if (showSuccessMessage) {
      const message = this.translationService.getTranslation('Connect', 'info.resetPassword');

      this.router.navigate([ '/authenticate' ], {
        state: {
          message: new VueServerMessage(VueServerMessageType.Success, message)
        }
      });
      return;
    }
    this.router.navigate([ '/authenticate' ]);
  }

  /**
   * Navigates to the Username lookup page
   */
  public goToUsernameLookup(): void {
    this.router.navigate([ '/usernamelookup' ]);
  }

  /**
     * Call the api to verify user details.
     */
  public verifyUser(): void {
    this.formError.invalidFormSubmit = false;
    // eslint-disable-next-line no-console
    this.messages = [];
    if (this.initiatePasswordResetForm.form.valid) {
      this.accountManagementService.validateUserAndGetSecurityQuestions(
        this.inputUserName,
        this.inputSiteID.toString()
      ).subscribe({
        error: (err) => this.apiFailure(err),
        next: (resp) => this.verifyUserSuccess(resp)
      }
      );
      this.formError.invalidFormSubmit = false;
    } else {
      this.formError.invalidFormSubmit = true;
    }
  }

  /**
     * Runs whenever we retrieve security questions
     * or activate the user
     * @param response HttpErrorResponse
     */
  public apiFailure(response: HttpErrorResponse): void {
    const code = response.error[ 0 ];
    let errorMessage;
    if (code.code === 'CAS.User.UserDoesNotExist') {
      errorMessage = this.translationService
        .getTranslation('Connect', 'CAS.User.IdentityNotVerified');
    } else {
      errorMessage = this.translationService.getTranslationFromServerCode(
        code
      );
    }
    this.messages = [ new VueServerMessage({
      messageText: errorMessage,
      scrollIntoView: true,
      type: VueServerMessageType.Error
    }) ];
  }

  /**
     * Users details have been successfully verified.
     * @param resp: VueSecurityQuestionPromptGroup[]
     */
  public verifyUserSuccess(resp: PasswordResetDetails): void {
    this.questionPromptGroups = resp.questions;
    this.appUserID = resp.appUserID;
    this.userEmail = resp.email;
    this.stepIndex = 1;
  }

  /**
     * Runs when we get a new set of answers return from the
     * security question component
     *
     * @param response VueSecurityQuestionFormOutput
     */
  public questionsAnswered(response: VueSecurityQuestionFormOutput): void {
    this.formError.invalidFormSubmit = false;
    if (response.valid) {
      this.securityQuestionAnswers = response.answers;
    } else {
      this.securityQuestionAnswers = null;
    }
  }

  /**
     * On submit of form based on user reset selection
     * password reset will start
     */
  public resetPassword(): void {
    if (this.isEmailSelection === 'true') {
      this.messages.length = 0;
      this.formError.invalidFormSubmit = false;
      this.accountManagementService.initiatePasswordReset(
        this.inputUserName,
        this.inputSiteID
      ).subscribe(
        () => this.goToLogin(true),
        (err: HttpErrorResponse) => this.apiFailure(err)
      );
    } else {
      this.verifySecurityQuestions();
    }
  }

  /**
     * Calls the API to verify security question answers and
     * return the username
     */
  public verifySecurityQuestions(): void {
    this.formError.invalidFormSubmit = false;
    this.messages = [];
    if (this.securityQuestionAnswers) {
      this.accountManagementService.verifySecurityQuestionAnswers(
        this.appUserID, 'NA',
        this.securityQuestionAnswers
      ).subscribe({
        error: (err) => this.apiFailure(err),
        next: () => this.router.navigate([ '/security-questions/change-password' ], {
          state: {
            appUserID: this.appUserID,
            securityQuestionAnswers: this.securityQuestionAnswers
          }
        })
      }
      );
    } else {
      this.formError.invalidFormSubmit = true;
    }
  }
}
